/********************************************************* {COPYRIGHT-TOP} ***
 * Licensed Materials - Property of IBM
 *
 * (C) Copyright IBM Corporation 2025
 *
 * All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 ********************************************************** {COPYRIGHT-END} **/

import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { cleanUpKey } from '../utils/common';
import classNames from 'classnames';
import { TabPanel, TabPanels, Tag, Accordion, AccordionItem, CodeSnippet, Select, SelectItem, Loading } from '@carbon/react';
import { useTranslation } from 'react-i18next';
import ViewableJSONDisplay from './ViewableJSONDisplay';
import SwaggerParser from '@apidevtools/swagger-parser';
import generator from '../utils/generator';
import { useApi } from '../contexts/ApiHandler';

const Webhook = ({ api, event }) => {
  const [webhooksArray, setWebhooksArray] = useState([]);
  const [example, setExample] = useState(null);
  const [examplesMap, setExamples] = useState(null);
  const [selectedExample, setSelectedExample] = useState();
  const { apiHandler } = useApi();
  const [schema, setSchema] = useState({});

  useEffect(() => {
    SwaggerParser.dereference(api);
    const webhooksArray = Object.keys(api.webhooks).map((key) => ({
      key,
      value: api.webhooks[key]
    }));

    setWebhooksArray(webhooksArray);
  }, [api]);

  useEffect(() => {
    if (schema && api && event && webhooksArray.length > 0) {
      const newSchema = api.webhooks?.[event]?.post?.requestBody.content['application/json'].schema;
      if (newSchema !== schema) {
        setSchema();
      }

      generator
        .generateWebhookExampleParameter({
          api: apiHandler.api,
          schema: schema,
          contentType: 'application/json'
        })
        .then(
          (data) => {
            if (data.hasMultipleExamples) {
              setExamples(data.example);
              setSelectedExample(Object.keys(data.example)[0]);
            } else {
              setExample(JSON.parse(data.example));
            }
          },
          (error) => {
            console.error(error);
          }
        );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, event, webhooksArray, schema]);

  const filteredWebhookArr = webhooksArray.filter((webhook) => webhook.key === event);
  const { t } = useTranslation();
  const onExampleChange = (evt, index) => {
    setSelectedExample(evt.target.value);
  };

  const ParametersRender = ({ details }) => {
    return (
      <div className="operationParameters">
        <div className="parameters">
          {details.parameters && Array.isArray(details.parameters) && details.parameters.length > 0 ? (
            <div className="parameters-container" data-testid="parameters-container">
              {details.parameters.map((param, index) => (
                <div key={index}>
                  <div className="labelText">
                    <strong>{param.name}</strong>
                    {param.required && (
                      <Tag size="sm" type="magenta">
                        {t('required')}
                      </Tag>
                    )}
                  </div>
                  <div className="valueText">
                    <span className="labelText">({param.in})</span>
                    <span>{param.description}</span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="no-parameters">No Parameters</p>
          )}
        </div>
      </div>
    );
  };

  const RequestBodyRender = ({ requestBody }) => {
    const contentHeader = requestBody.content['application/json'];

    useEffect(() => {
      if (contentHeader.schema) {
        setSchema(contentHeader.schema);
      }
    }, [contentHeader.schema, requestBody]);

    if (!requestBody || !requestBody.content || !contentHeader) {
      return;
    }

    return (
      <div data-testid="webhook-request-body">
        <Accordion>
          <AccordionItem title={t('schema')} open={true} data-testid="webhook-schema">
            <CodeSnippet type="multi">
              <ViewableJSONDisplay content={schema} />
            </CodeSnippet>
          </AccordionItem>
          <AccordionItem title={examplesMap ? t('examples') : t('example')} open={true}>
            {examplesMap && (
              <Select
                data-testid="example-selector"
                hideLabel
                className="example-selector"
                onChange={onExampleChange}
                value={selectedExample}
                id="example-selector"
              >
                {Object.keys(examplesMap).map((key) => {
                  return <SelectItem key={key} text={key} value={key} />;
                })}
              </Select>
            )}
            <CodeSnippet type="multi">
              <div>
                {examplesMap ? (
                  <div>{selectedExample && <ViewableJSONDisplay content={JSON.parse(examplesMap[selectedExample])} />}</div>
                ) : (
                  <div>
                    {example === null && <Loading small withOverlay={false} data-testid={'loading-example'} />}
                    {example !== null && <ViewableJSONDisplay content={example} />}
                  </div>
                )}
              </div>
            </CodeSnippet>
          </AccordionItem>
        </Accordion>
      </div>
    );
  };

  const ResponsesRender = ({ responses }) => {
    const [selectedResponse, setSelectedResponse] = useState(Object.keys(responses)[0]);

    if (!responses) {
      return <p>{t('no_responses_available')}</p>;
    }

    const handleSelectChange = (e) => {
      const response = e.target.value;
      setSelectedResponse(response);
    };

    return (
      <div>
        <Select hideLabel id="operationResponses" aria-label={t('select_response_type')} onChange={handleSelectChange} data-testid="response-selector">
          {Object.keys(responses)
            .sort()
            .map((response) => (
              <SelectItem key={response} value={response} text={response} />
            ))}
        </Select>
        {selectedResponse && (
          <div className="responsesContainer response-code-200">
            <div className="markdown responseDescription" data-testid="response-description">
              <p>{responses[selectedResponse]?.description}</p>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div
      className={classNames(
        'apiconnect-explorer',
        'definitions-section',
        'main',
        'rest-api',
        `definitions-${cleanUpKey(api.info.title + '_' + api.info.version)}`,
        'webhook-container'
      )}
    >
      <section className="definitionsHeader" aria-label="definitions-header">
        <h2>
          {t('webhook')} {<Tag className="apiSpecLabel">{'OAS3.1'}</Tag>}
        </h2>
      </section>
      <TabPanels>
        <TabPanel>
          {filteredWebhookArr.map(({ key, value }) => (
            <div style={{ marginBottom: 30 }} key={key}>
              {Object.entries(value).map(([method, details]) => (
                <div key={method}>
                  <div>
                    <div role="heading" aria-level={3} className="labelText">
                      <p className="labelText method post">{method.toUpperCase()}</p>
                    </div>
                  </div>
                  <div className="operationInformation operationType divided__item" data-testid={'operation-type'}>
                    <div role="heading" aria-level={3} className="labelText">
                      {t('event')}
                    </div>
                    <div className="valueText" data-testid="event-name">
                      {key}
                    </div>
                  </div>
                  {details.description && (
                    <div className="operationInformation operationType divided__item" data-testid={'operation-type'}>
                      <div role="heading" aria-level={3} className="labelText">
                        {t('description')}
                      </div>
                      <div className="valueText">{details?.description}</div>
                    </div>
                  )}
                  <div className="operationInformation operationSummary operationParms divided__item">
                    <div role="heading" aria-level={3} className="labelText">
                      {t('parameters')}
                    </div>
                    <ParametersRender details={details} />
                  </div>
                  <div className="operationInformation operationType" data-testid={'operation-type'}>
                    <div role="heading" aria-level={3} className="labelText">
                      {t('request_body')}
                    </div>
                    {details.requestBody && <RequestBodyRender requestBody={details.requestBody} />}
                  </div>
                  <div className="operationInformation operationType" data-testid={'operation-type'}>
                    <div role="heading" aria-level={3} className="labelText">
                      {'Responses'}
                    </div>
                    {details.responses && <ResponsesRender responses={details.responses} />}
                  </div>
                </div>
              ))}
            </div>
          ))}
        </TabPanel>
      </TabPanels>
    </div>
  );
};

Webhook.propTypes = {
  api: PropTypes.object,
  event: PropTypes.string
};

export default Webhook;
